import React from 'react';
import {
  ClerkProvider,
  SignIn,
  SignUp,
} from '@clerk/clerk-react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import DashboardLayout from './components/layout/DashboardLayout';
import AuthMiddleware from './components/AuthMiddleware';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/dashboard';
import Onboarding from '@/pages/Onboarding';
import { UserRole } from '@/types/user';

const queryClient = new QueryClient();
const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey) {
  throw new Error("Missing Clerk Publishable Key");
}

const ClerkWithRoutes: React.FC = () => {
  const navigate = useNavigate();

  return (
    <ClerkProvider 
      publishableKey={clerkPubKey}
      navigate={(to) => navigate(to)}
    >
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/sign-in/*" element={<SignIn routing="path" path="/sign-in" />} />
        <Route path="/sign-up/*" element={<SignUp routing="path" path="/sign-up" />} />
        
        {/* Protected Routes */}
        <Route
          path="/onboarding"
          element={
            <AuthMiddleware requireOnboarding={false}>
              <Onboarding />
            </AuthMiddleware>
          }
        />

        <Route
          path="/dashboard/*"
          element={
            <AuthMiddleware allowedRoles={['admin', 'coach', 'swimmer', 'parent']}>
              <DashboardLayout>
                <Dashboard />
              </DashboardLayout>
            </AuthMiddleware>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin/*"
          element={
            <AuthMiddleware allowedRoles={['admin']}>
              <DashboardLayout>
                {/* Admin components */}
              </DashboardLayout>
            </AuthMiddleware>
          }
        />

        {/* Fallback Routes */}
        <Route path="/unauthorized" element={<div>Unauthorized Access</div>} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      <Toaster position="top-right" />
    </ClerkProvider>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <ClerkWithRoutes />
      </Router>
    </QueryClientProvider>
  );
};

export default App; 